import { Directive, ElementRef, forwardRef, HostListener, Input, isDevMode } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

@Directive({
  selector: 'input[lmsNumbersOnly]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumbersOnlyDirective),
      multi: true,
    },
  ],
})
export class NumbersOnlyDirective implements Validator {
  @Input() allowDecimals = false;
  @Input() allowNegative = false;
  @Input() allowPaste: boolean = true;

  private readonly decimalSeparator = '.';
  private readonly negativeSign = '-';

  constructor(private el: ElementRef<HTMLInputElement>) {}

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const valid = this.isValidNumber(control.value);
    return valid ? null : { numbersOnly: true };
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const { key } = event;

    // Allow special keys
    if (this.isSpecialKey(event)) {
      return;
    }

    // Allow negative sign at start
    if (
      this.allowNegative &&
      key === this.negativeSign &&
      input.selectionStart === 0 &&
      !input.value.includes(this.negativeSign)
    ) {
      return;
    }

    // Allow decimal separator
    if (this.allowDecimals && key === this.decimalSeparator && !input.value.includes(this.decimalSeparator)) {
      return;
    }

    // Allow only numbers
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.allowPaste && !isDevMode()) {
      event.preventDefault();
    }
  }

  private isSpecialKey(event: KeyboardEvent): boolean {
    const specialKeys = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];

    // Allow copy/paste shortcuts
    if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x', 'a'].includes(event.key)) {
      return true;
    }

    return specialKeys.includes(event.key);
  }

  private isValidNumber(value: string): boolean {
    const pattern = this.buildValidationPattern();
    return new RegExp(pattern).test(value);
  }

  private buildValidationPattern(): string {
    let pattern = '^';

    if (this.allowNegative) {
      pattern += '-?';
    }

    pattern += '\\d*';

    if (this.allowDecimals) {
      pattern += `(\\.\\d*)?`;
    }

    pattern += '$';

    return pattern;
  }
}
